import { Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { goneId, useRecordVisit } from '../../../lib/use-record-visit';
import { useCurrentSession } from '../../../stores/session';
import { useCandidatePanelProfile } from '../api/candidate-panel-api';
import { DoNotRehireButton, MergeWithButton } from '../components/panel/CandidateActions';
import { CandidatePanel, isCandidatePanelTab, type CandidatePanelTab } from '../components/panel/CandidatePanel';
import styles from './CandidateProfile.module.css';

/** The full profile's address: `/candidates/{id}?tab=jobs`. CV when the address names no tab. */
export interface CandidateProfileSearch {
  readonly tab?: CandidatePanelTab;
}

export function readCandidateProfileSearch(search: Readonly<Record<string, unknown>>): CandidateProfileSearch {
  return isCandidatePanelTab(search.tab) && search.tab !== 'cv' ? { tab: search.tab } : {};
}

/**
 * CAND-02 — the permanent person record (E5), as the full page of the candidate pop-up
 * (Candidate-profile.dc.html): back to the list, the person-level actions, then the panel. The
 * tab lives in the address (06-qol §6). Applications outside the viewer's scope stay listed with
 * venture and stage only (D1).
 */
export function CandidateProfilePage({
  candidateId,
  tab,
  onTabChange,
}: {
  readonly candidateId: string;
  readonly tab: CandidatePanelTab;
  readonly onTabChange: (tab: CandidatePanelTab) => void;
}): ReactNode {
  const profile = useCandidatePanelProfile(candidateId);
  // A hiring manager reaches a person through their own job, never the list (Group 1 Q1 (a)).
  const listLevel = useCurrentSession()?.permissions.candidate?.level;
  const mayBrowse = listLevel === 'full' || listLevel === 'read';
  const data = profile.data?.candidate.id === candidateId ? profile.data : undefined;
  useRecordVisit(data ? { kind: 'candidate', id: candidateId, title: data.candidate.fullName, subtitle: data.candidate.currentTitle, path: `/candidates/${candidateId}` } : null, goneId(profile.error, candidateId));

  return (
    <div className={`${styles.page} ${styles.panelPage}`}>
      <div className={styles.pageBar}>
        {mayBrowse && (
          <Link to="/candidates" className={styles.backLink}>
            <ArrowLeft size={14} aria-hidden />
            Candidates
          </Link>
        )}
        <span className={styles.pageBarSpacer} />
        {data?.can.merge && !data.candidate.mergedIntoCandidateId && <MergeWithButton candidate={data.candidate} size="sm" />}
        {data?.can.setDoNotRehire && <DoNotRehireButton candidate={data.candidate} size="sm" />}
      </div>
      <CandidatePanel candidateId={candidateId} variant="page" tab={tab} onTabChange={onTabChange} />
    </div>
  );
}
